import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, ArrowRight, Repeat } from "lucide-react";
import {
  fetchEvents,
  formatEventDate,
  pickField,
  eventCategoryLabel,
  EVENT_CATEGORY_META,
} from "@/api/events";
import { LLink } from "@/i18n/LLink";
import { useLang } from "@/i18n/LangContext";
import { pageContent, pickLang } from "@/i18n/pageContent";

// Meertalige labels voor de seizoens-teaser
const THIS_MONTH_EYEBROW = { nl: "Deze maand", de: "Diesen Monat", en: "This month" };
const IN_LEMMER = { nl: "in Lemmer", de: "in Lemmer", en: "in Lemmer" };
const SEASONAL_INTRO = {
  nl: "Wat is er deze maand te beleven? Een greep uit het lokale programma.",
  de: "Was ist diesen Monat los? Ein Auszug aus dem lokalen Programm.",
  en: "What's happening this month? A selection from the local programme.",
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Krijg de maandnaam ("juli"/"Juli"/"July") in de juiste taal
const getMonthName = (lang, date = new Date()) => {
  const locale = lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "nl-NL";
  try {
    return new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
  } catch {
    return "";
  }
};

// Bepaal of een event 'deze maand' relevant is
const isEventInCurrentMonth = (ev) => {
  if (!ev?.date) return false;
  // Terugkerende events (weekmarkt etc.) horen iedere maand thuis
  if (ev.recurring) return true;
  const start = new Date(`${ev.date}T00:00:00`);
  const end = ev.end_date ? new Date(`${ev.end_date}T23:59:59`) : start;
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  // Overlap tussen [start,end] en huidige maand
  return start <= monthEnd && end >= monthStart;
};

const CatTag = ({ category, lang }) => {
  const key = String(category || "").toLowerCase();
  const meta = EVENT_CATEGORY_META[key] || EVENT_CATEGORY_META.default;
  const label = eventCategoryLabel(category, lang) || "Evenement";
  return (
    <span
      className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-[Poppins] font-semibold shadow-md"
      style={{ backgroundColor: meta.color, color: meta.textColor }}
    >
      {label}
    </span>
  );
};

export const AgendaTeaserSection = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const c = pickLang(pageContent.home, lang).agenda;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Haal ruim events op zodat we deze maand kunnen filteren
        const data = await fetchEvents(20);
        if (!cancelled) setEvents(data);
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Bepaal welke set we tonen: eerst events deze maand, anders val terug op eerstvolgende.
  const { displayedEvents, seasonal } = useMemo(() => {
    const thisMonth = events.filter(isEventInCurrentMonth);
    if (thisMonth.length >= 2) {
      return { displayedEvents: thisMonth.slice(0, 3), seasonal: true };
    }
    return { displayedEvents: events.slice(0, 3), seasonal: false };
  }, [events]);

  const monthName = getMonthName(lang);
  const seasonalTitle = `${capitalize(monthName)} ${IN_LEMMER[lang] || IN_LEMMER.nl}`;
  const eyebrow = seasonal ? THIS_MONTH_EYEBROW[lang] || THIS_MONTH_EYEBROW.nl : c.eyebrow;
  const title = seasonal ? seasonalTitle : c.title;
  const intro = seasonal ? SEASONAL_INTRO[lang] || SEASONAL_INTRO.nl : c.intro;

  return (
    <section
      data-testid="home-agenda-teaser"
      data-seasonal={seasonal ? "true" : "false"}
      className="ll-section-soft py-16 sm:py-20"
    >
      <div className="ll-container">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10 sm:mb-14">
          <div className="max-w-2xl">
            <span
              data-testid="agenda-teaser-eyebrow"
              className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3"
            >
              {eyebrow}
            </span>
            <h2
              data-testid="agenda-teaser-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold"
            >
              {title}
            </h2>
            <p className="mt-3 text-[#3E5871] font-[Inter] text-base sm:text-lg">
              {intro}
            </p>
          </div>
          <LLink
            to="/agenda"
            data-testid="agenda-view-all"
            className="ll-btn-outline shrink-0"
          >
            {t("common.volledige_agenda")}
            <ArrowRight className="w-4 h-4" />
          </LLink>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden border border-[#E4EEF5] aspect-[16/22] animate-pulse"
              />
            ))}
          </div>
        ) : displayedEvents.length === 0 ? (
          <p className="text-[#5A7594] font-[Inter]">
            {t("common.geen_evenementen")}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {displayedEvents.map((ev) => (
              <article
                key={ev.slug}
                data-testid={`event-card-${ev.slug}`}
                className="bg-white rounded-2xl overflow-hidden border border-[#E4EEF5] hover:shadow-xl transition-shadow duration-300 flex flex-col"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[#F4F8FB]">
                  {ev.image ? (
                    <img
                      src={ev.image}
                      alt={pickField(ev, "title", lang)}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : null}
                  <CatTag category={ev.category} lang={lang} />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-wider mb-2">
                    {ev.recurring ? (
                      <Repeat className="w-4 h-4" />
                    ) : (
                      <Calendar className="w-4 h-4" />
                    )}
                    {formatEventDate(ev, lang)}
                  </div>
                  <h3 className="text-xl font-[Poppins] font-bold mb-2 text-[#173A5E]">
                    {pickField(ev, "title", lang)}
                  </h3>
                  {pickField(ev, "location", lang) && (
                    <p className="text-[#5A7594] text-sm font-[Inter] mb-3">
                      {pickField(ev, "location", lang)}
                    </p>
                  )}
                  {pickField(ev, "description", lang) && (
                    <p className="text-[#3E5871] font-[Inter] text-sm leading-relaxed flex-1 line-clamp-4">
                      {pickField(ev, "description", lang)}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AgendaTeaserSection;
